import React from 'react';
import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import Button from '@mui/material/Button';
import CardContent from '@mui/material/CardContent';
import Avatar from '@mui/material/Avatar';
import Grid from '@mui/material/Grid';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Container from '../../../GeneralComponents/Container';
import { useNavigate } from 'react-router';

const COMMON_FEATURES = [
  // 'Track Student Location',
  'Track Student Attendance',
  'Manage Drivers',
  'Track Expenses',
  'Completion Certificate and Report card',
  'Driving Videos',
  'Upto 1% cashback on HPCL Fuel Card',
  'Web portal access for organizations',
  'Dedicated mobile app for drivers & students',
];

/* const plans = [
  {
    title: 'Starter',
    subtitle: 'Perfect for small driving schools',
    price: '399',
    instructors: '1',
    students: '15',
    hpclFuelCard: false,
    isHighlighted: false,
    headerBg: 'linear-gradient(135deg, #607d8b 0%, #455a64 100%)',
  },
  {
    title: 'Premium',
    subtitle: 'For growing driving schools',
    price: '599',
    instructors: '4',
    students: '40',
    hpclFuelCard: true,
    isHighlighted: true,
    headerBg: 'linear-gradient(135deg, #1976d2 0%, #0d47a1 100%)',
  },
  {
    title: 'Premium Plus',
    subtitle: 'For established driving schools',
    price: '799',
    instructors: '7',
    students: '100+',
    hpclFuelCard: true,
    isHighlighted: false,
    headerBg: 'linear-gradient(135deg, #4a148c 0%, #1a0050 100%)',
  },
]; */

const plans = [
  {
    title: 'Essentials',
    subtitle: 'Everything you need to run your driving school',
    price: 'Free',
    instructors: 'Unlimited',
    students: 'Unlimited',
    hpclFuelCard: true,
    isHighlighted: true,
    headerBg: 'linear-gradient(135deg, #1976d2 0%, #0d47a1 100%)',
  },
];

const StatBox = ({ value, label }) => (
  <Box sx={{ textAlign: 'center', flex: 1, py: 1 }}>
    <Typography
      sx={{
        fontSize: { xs: '1.6rem', md: '2rem' },
        fontWeight: 900,
        lineHeight: 1,
        color: 'text.primary',
        letterSpacing: '-1px',
      }}
    >
      {value}
    </Typography>
    <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>
      {label}
    </Typography>
  </Box>
);

const CheckIcon = () => (
  <svg width={12} height={12} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
    <path
      fillRule="evenodd"
      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
      clipRule="evenodd"
    />
  </svg>
);

const Main = () => {
  const navigate = useNavigate();
  const theme = useTheme();

  return (
    <Box>
      {/* Page header */}
      <Box
        sx={{
          position: 'relative',
          backgroundColor: theme.palette.alternate.main,
          backgroundImage: `linear-gradient(120deg, ${theme.palette.background.paper} 0%, ${theme.palette.alternate.main} 100%)`,
          marginTop: -13,
          paddingTop: 13,
          paddingBottom: 4,
        }}
      >
        <Container position={'relative'} zIndex={3}>
          <Box marginBottom={4}>
            <Typography variant="h3" gutterBottom align={'center'} sx={{ fontWeight: 900 }}>
              Pricing
            </Typography>
            <Typography variant="h6" align={'center'} color={'text.secondary'}>
              Pick the best plan based on your requirement
            </Typography>
          </Box>
        </Container>
      </Box>

      <Container>
        <Grid container spacing={3} justifyContent={'center'} alignItems={'stretch'}>
          {plans.map((item, i) => (
            <Grid item xs={12} md={6} key={i} sx={{ display: 'flex' }}>
              <Box
                component={Card}
                width={1}
                display={'flex'}
                flexDirection={'column'}
                elevation={item.isHighlighted ? 8 : 1}
                sx={{
                  borderRadius: 3,
                  overflow: 'hidden',
                  transform: item.isHighlighted ? { md: 'scale(1.04)' } : 'none',
                  border: item.isHighlighted ? `2px solid ${theme.palette.primary.main}` : '1px solid',
                  borderColor: item.isHighlighted ? theme.palette.primary.main : 'divider',
                }}
              >
                {/* Coloured header */}
                <Box
                  sx={{
                    background: item.headerBg,
                    px: 3,
                    pt: 3,
                    pb: 3,
                    position: 'relative',
                  }}
                >
                  <Typography variant="overline" sx={{ color: 'rgba(255,255,255,0.7)', fontWeight: 700, letterSpacing: 2 }}>
                    {item.title}
                  </Typography>
                  <Box display={'flex'} alignItems={'flex-end'} gap={0.5} mt={0.5}>
                    {item.price !== 'Free' && (
                      <Typography sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '1.2rem', lineHeight: 1.8 }}>₹</Typography>
                    )}
                    <Typography sx={{ color: 'white', fontSize: { xs: '3rem', md: '3.5rem' }, fontWeight: 900, lineHeight: 1 }}>
                      {item.price}
                    </Typography>
                    {item.price !== 'Free' && (
                      <Typography sx={{ color: 'rgba(255,255,255,0.7)', mb: 0.5 }}>/mo</Typography>
                    )}
                  </Box>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)' }}>
                    {item.price === 'Free' ? 'No credit card required' : 'Inclusive of GST'}
                  </Typography>
                </Box>

                <CardContent sx={{ px: 3, pt: 0, pb: 0, flexGrow: 1 }}>
                  {/* Limits */}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      mt: 2,
                      mb: 2,
                      bgcolor: 'background.paper',
                      borderRadius: 2,
                      boxShadow: '0 2px 12px rgba(0,0,0,0.10)',
                      border: '1px solid',
                      borderColor: 'divider',
                      px: 2,
                      py: 1.5,
                    }}
                  >
                    <StatBox value={item.instructors} label="Instructors" />
                    <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
                    <StatBox value={item.students} label="Students" />
                  </Box>

                  {/* HPCL badge */}
                  {item.hpclFuelCard && (
                    <Box mb={2}>
                      <Chip
                        label="⛽  HPCL Fuel Card Eligible"
                        size="small"
                        sx={{
                          bgcolor: '#7B1F1F',
                          color: 'white',
                          fontWeight: 700,
                          fontSize: '0.72rem',
                        }}
                      />
                    </Box>
                  )}

                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>
                    What's included
                  </Typography>

                  {/* Features */}
                  <Grid container spacing={0.5} sx={{ mb: 2 }}>
                    {COMMON_FEATURES.map((feature, j) => (
                      <Grid item xs={12} key={j}>
                        <Box component={ListItem} disableGutters width={'auto'} padding={0}>
                          <Box component={ListItemAvatar} minWidth={'auto !important'} marginRight={1.5}>
                            <Box
                              component={Avatar}
                              bgcolor={theme.palette.primary.main}
                              width={18}
                              height={18}
                            >
                              <CheckIcon />
                            </Box>
                          </Box>
                          <ListItemText
                            primary={feature}
                            primaryTypographyProps={{ variant: 'body2' }}
                          />
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </CardContent>

                <CardActions sx={{ px: 3, pb: 3, pt: 1 }}>
                  <Button
                    onClick={() => navigate('/signup')}
                    size={'large'}
                    variant={item.isHighlighted ? 'contained' : 'outlined'}
                    fullWidth
                    sx={{ borderRadius: 2, fontWeight: 700 }}
                  >
                    Get Started for Free
                  </Button>
                </CardActions>
              </Box>
            </Grid>
          ))}
        </Grid>

        {/* Per-branch note */}
        <Box
          mt={6}
          mb={2}
          sx={{
            borderRadius: 2,
            border: '1px solid',
            borderColor: theme.palette.primary.main,
            bgcolor: theme.palette.alternate.main,
            py: 2.5,
            px: 3,
            textAlign: 'center',
          }}
        >
          <Typography variant={'body1'} fontWeight={600}>
            📍 This plan is per branch
          </Typography>
          <Typography variant={'body2'} color={'text.secondary'} mt={0.5} fontWeight={600}>
            If you have more than 1 branch, you need to signup separately for each branch.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default Main;
